import { useEffect, useState } from 'react';

import {
    ExternalSocialProvider,
    getDefaultExternalSocialProviders,
    getExternalSocialProviders,
    getSocialRegistrationIssuer,
} from 'common/api/auth/socialRegistration';

interface SocialRegistrationProviderDiscovery {
    providers: ExternalSocialProvider[];
    isLoading: boolean;
}

let discoveredProviders: ExternalSocialProvider[] | null = null;
let providerDiscovery: Promise<ExternalSocialProvider[]> | null = null;

const cacheDiscoveredProviders = (providerOptions: ExternalSocialProvider[]) => {
    discoveredProviders = providerOptions;
    return providerOptions;
};

export const discoverSocialRegistrationProviders = () => {
    if (discoveredProviders) {
        return Promise.resolve(discoveredProviders);
    }

    if (!providerDiscovery) {
        try {
            const issuer = getSocialRegistrationIssuer();
            providerDiscovery = getExternalSocialProviders(issuer)
                .then(cacheDiscoveredProviders)
                .catch(() => cacheDiscoveredProviders(getDefaultExternalSocialProviders(issuer)));
        } catch (_error) {
            providerDiscovery = Promise.resolve(
                cacheDiscoveredProviders(getDefaultExternalSocialProviders())
            );
        }
    }

    return providerDiscovery;
};

export const useSocialRegistrationProviderDiscovery = (): SocialRegistrationProviderDiscovery => {
    const [providerDiscoveryState, setProviderDiscoveryState] =
        useState<SocialRegistrationProviderDiscovery>({
            providers: discoveredProviders ?? [],
            isLoading: discoveredProviders === null,
        });

    useEffect(() => {
        let isActive = true;

        discoverSocialRegistrationProviders().then((providerOptions) => {
            if (isActive) {
                setProviderDiscoveryState({
                    providers: providerOptions,
                    isLoading: false,
                });
            }
        });

        return () => {
            isActive = false;
        };
    }, []);

    return providerDiscoveryState;
};

export const useSocialRegistrationProviders = () =>
    useSocialRegistrationProviderDiscovery().providers;
